import { Component, ChangeDetectionStrategy, input, output, signal, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DamageView, DiagramShape, diagramAsset, mirrorView, shapesFor, viewBoxFor } from './vehicle-damage-diagrams';
import { canonicalPartId, formatVehiclePartLabel, normalizeVehicleType } from './vehicle-damage-parts';
import { VehicleDamageConfigService, VehicleDamageViews } from '../services/vehicle-damage-config.service';

@Component({
  selector: 'app-vehicle-damage-selector',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="damage-selector" [class.compact]="compact()" [class.tabs-top]="tabsOnTop()">
      <div class="damage-main">
        <div class="view-tabs" role="tablist" [attr.aria-orientation]="tabsOnTop() ? 'horizontal' : 'vertical'">
          @for (tab of views; track tab.id) {
            <button type="button" class="view-tab" role="tab"
              [class.active]="activeView() === tab.id"
              [attr.aria-selected]="activeView() === tab.id"
              (click)="activeView.set(tab.id)">
              {{ tab.label }}
            </button>
          }
        </div>

        <svg class="diagram" [attr.viewBox]="frame()" role="img" [attr.aria-label]="'Vista ' + activeLabel()">
          <g [attr.transform]="mirrorTransform()">
            @if (artUrl(); as url) {
              <image [attr.href]="url" x="0" y="0" [attr.width]="artWidth()" [attr.height]="artHeight()" class="art" />
            }
              @for (shape of currentShapes(); track shape.id || $index) {
              @switch (shape.kind) {
                @case ('path') {
                  <path [attr.d]="shape.d" [attr.class]="shapeClass(shape)"
                    [style.fill]="shape.selected ? 'rgba(220, 53, 69, 0.48)' : null"
                    [style.stroke]="shape.selected ? '#b91c1c' : null"
                    [style.stroke-width]="shape.selected ? 2 : null"
                    [attr.title]="shape.id ? label(shape.id) : null"
                    (click)="toggle(shape.id, $event)" />
                }
                @case ('circle') {
                  <circle [attr.cx]="shape.cx" [attr.cy]="shape.cy" [attr.r]="shape.r" [attr.class]="shapeClass(shape)"
                    [style.fill]="shape.selected ? 'rgba(220, 53, 69, 0.48)' : null"
                    [style.stroke]="shape.selected ? '#b91c1c' : null"
                    [style.stroke-width]="shape.selected ? 2 : null"
                    [attr.title]="shape.id ? label(shape.id) : null"
                    (click)="toggle(shape.id, $event)" />
                }
                @default {
                  <ellipse [attr.cx]="shape.cx" [attr.cy]="shape.cy" [attr.rx]="shape.rx" [attr.ry]="shape.ry" [attr.class]="shapeClass(shape)"
                    [style.fill]="shape.selected ? 'rgba(220, 53, 69, 0.48)' : null"
                    [style.stroke]="shape.selected ? '#b91c1c' : null"
                    [style.stroke-width]="shape.selected ? 2 : null"
                    [attr.title]="shape.id ? label(shape.id) : null"
                    (click)="toggle(shape.id, $event)" />
                }
              }
            }
          </g>
        </svg>
      </div>

      @if (showSelectedList()) {
        <div class="chips">
          @if (selectedIds().length) {
            @for (id of selectedIds(); track id) {
              <span class="chip">
                {{ label(id) }}
                @if (!readOnly()) {
                  <button type="button" class="chip-remove" [attr.aria-label]="'Quitar ' + label(id)" (click)="toggle(id, $event)">×</button>
                }
              </span>
            }
          } @else {
            <span class="empty">{{ readOnly() ? 'Sin partes afectadas' : 'Seleccione las partes dañadas en el esquema' }}</span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; height: 100%; }
    .damage-selector { display: flex; flex-direction: column; gap: 10px; flex: 1 1 auto; min-height: 0; }
    .damage-selector.compact { gap: 6px; }
    .damage-main {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 8px;
      flex: 1 1 auto;
      min-height: 0;
    }
    .tabs-top .damage-main {
      flex-direction: column;
      align-items: center;
    }
    .view-tabs {
      display: flex;
      flex-direction: column;
      flex: 0 0 auto;
      gap: 6px;
      justify-content: center;
    }
    .tabs-top .view-tabs {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;
    }
    .view-tab {
      border: 1px solid var(--bs-border-color, #ced4da);
      background: var(--bs-component-bg, #fff);
      color: var(--bs-body-color, #344054);
      border-radius: 999px;
      padding: 6px 10px;
      font-size: 11px;
      font-weight: 600;
      writing-mode: horizontal-tb;
      white-space: nowrap;
      min-width: 4.5rem;
      text-align: center;
    }
    .compact .view-tab { padding: 4px 8px; font-size: 10px; min-width: 4rem; }
    .view-tab.active { background: #0d6efd; border-color: #0d6efd; color: #fff; }
    .diagram {
      flex: 1 1 auto;
      width: 100%;
      height: auto;
      max-height: 460px;
      min-width: 0;
      background: transparent;
      border-radius: 8px;
    }
    .compact .damage-main {
      align-items: center;
      justify-content: flex-start;
      flex: 0 1 auto;
      width: 100%;
    }
    .compact .diagram {
      flex: 0 1 auto;
      width: auto;
      max-width: 100%;
      max-height: 200px;
      min-height: 0;
      align-self: center;
    }
    .damage-selector.compact {
      justify-content: flex-start;
      align-items: stretch;
    }
    .compact .view-tabs {
      justify-content: center;
      align-self: stretch;
    }
    .art { pointer-events: none; }
    :host-context([data-bs-theme="dark"]) .art { filter: invert(1); }
    .tone-shadow { fill: none; stroke: #d0d5dd; stroke-width: 1.2; pointer-events: none; }
    .tone-paint { fill: #fff; stroke: #1a1a1a; stroke-width: 1.8; stroke-linejoin: round; stroke-linecap: round; }
    .tone-glass { fill: #d7e6ee; stroke: #1a1a1a; stroke-width: 1.45; cursor: pointer; }
    .tone-tire { fill: #2c2c2c; stroke: #111; stroke-width: 1.5; cursor: pointer; }
    .tone-rim { fill: #f4f4f4; stroke: #222; stroke-width: 1.3; pointer-events: none; }
    .tone-hub { fill: #4a4a4a; stroke: #222; stroke-width: 1; pointer-events: none; }
    .tone-light { fill: #f7f1d8; stroke: #1a1a1a; stroke-width: 1.35; cursor: pointer; }
    .tone-tail { fill: #f4c7c7; stroke: #1a1a1a; stroke-width: 1.35; cursor: pointer; }
    .tone-trim { fill: #ececec; stroke: #1a1a1a; stroke-width: 1.5; }
    .tone-grille { fill: #fff; stroke: #1a1a1a; stroke-width: 1.3; pointer-events: none; }
    .tone-chrome { fill: #fff; stroke: #1a1a1a; stroke-width: 1.3; }
    .tone-seam { fill: none; stroke: #1a1a1a; stroke-width: 1.35; stroke-linecap: round; pointer-events: none; }
    .tone-hit { fill: transparent; stroke: rgba(13, 110, 253, 0.15); stroke-width: 1.2; cursor: pointer; transition: fill 0.15s, stroke 0.15s; }
    .decor { pointer-events: none; }
    .tone-hit:hover { fill: rgba(13, 110, 253, 0.28); stroke: #0d6efd; stroke-width: 1.8; }
    .tone-glass:hover, .tone-light:hover, .tone-tail:hover, .tone-tire:hover { filter: brightness(1.08); }
    .hot { cursor: pointer; }
    .tone-paint.hot:hover, .tone-trim.hot:hover, .tone-chrome.hot:hover { filter: brightness(0.96); }
    .selected { fill: #dc3545 !important; stroke: #9b1c2c; }
    .tone-hit.selected { fill: rgba(220, 53, 69, 0.48) !important; stroke: #b91c1c !important; stroke-width: 2; }
    .readonly, .tone-shadow, .tone-rim, .tone-hub, .tone-seam, .tone-grille { cursor: default; }
    .chips { display: flex; flex-wrap: wrap; gap: 6px; min-height: 28px; }
    .chip {
      display: inline-flex; align-items: center; gap: 6px;
      background: #fde8ea; color: #842029; border-radius: 999px;
      padding: 2px 8px; font-size: 12px; font-weight: 600;
    }
    .chip-remove { border: 0; background: transparent; color: #842029; font-size: 14px; line-height: 1; padding: 0; }
    .empty { color: var(--bs-secondary-color, #6c757d); font-size: 12px; }
  `]
})
export class VehicleDamageSelectorComponent {
  private configService = inject(VehicleDamageConfigService);

  vehicleType = input<string>('sedan');
  initialSelectedParts = input<string[]>([]);
  readOnly = input<boolean>(false);
  showSelectedList = input<boolean>(true);
  compact = input<boolean>(false);
  /** Pestañas de vista en fila arriba (útil en modo VIEW). */
  tabsOnTop = input<boolean>(false);
  partsSelected = output<string[]>();

  readonly views: { id: DamageView; label: string }[] = [
    { id: 'front', label: 'Frente' },
    { id: 'rear', label: 'Trasera' },
    { id: 'left', label: 'Izquierda' },
    { id: 'right', label: 'Derecha' }
  ];

  activeView = signal<DamageView>('left');
  selectedIds = signal<string[]>([]);
  configViews = signal<VehicleDamageViews | null>(null);

  bodyType = computed(() => normalizeVehicleType(this.vehicleType()));
  currentShapes = computed(() => {
    const view = this.activeView();
    const key = view === 'front' || view === 'rear' ? view : 'side';
    const configured = this.configViews()?.[key] as DiagramShape[] | undefined;
    const shapes = shapesFor(this.bodyType(), view, configured);
    const selected = this.selectedIds();
    return shapes
      .map(shape => ({ ...shape, selected: !!shape.id && selected.includes(shape.id) }))
      .sort((a, b) => Number(a.selected) - Number(b.selected));
  });
  currentAsset = computed(() => diagramAsset(this.bodyType(), this.activeView()));
  frame = computed(() => viewBoxFor(this.bodyType(), this.activeView()));
  artUrl = computed(() => this.currentAsset()?.url ?? null);
  artWidth = computed(() => this.currentAsset()?.width ?? 0);
  artHeight = computed(() => {
    const box = this.frame().split(' ');
    return Number(box[3] ?? 0);
  });
  activeLabel = computed(() => this.views.find(v => v.id === this.activeView())?.label ?? '');
  mirrored = computed(() => mirrorView(this.activeView()));
  mirrorTransform = computed(() => {
    if (!this.mirrored()) return null;
    const width = this.frame().split(' ')[2] ?? '1040';
    return `translate(${width} 0) scale(-1 1)`;
  });

  constructor() {
    effect((onCleanup) => {
      const type = this.bodyType();
      const sub = this.configService.getShapes(type).subscribe(data => this.configViews.set(data));
      onCleanup(() => sub.unsubscribe());
    }, { allowSignalWrites: true });

    effect(() => {
      const incoming = [...new Set((this.initialSelectedParts() ?? []).map(id => canonicalPartId(id)))];
      const current = this.selectedIds();
      const same = incoming.length === current.length && incoming.every(id => current.includes(id));
      if (!same) this.selectedIds.set(incoming);
    }, { allowSignalWrites: true });
  }

  shapeClass(shape: { id: string; tone: string; selected?: boolean }): string {
    const hot = shape.id ? 'hot' : 'decor';
    const selected = shape.selected ? ' selected' : '';
    const readonly = this.readOnly() ? ' readonly' : '';
    return `tone-${shape.tone} ${hot}${selected}${readonly}`;
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  label(id: string): string {
    return formatVehiclePartLabel(id, this.bodyType());
  }

  toggle(id: string, event?: Event) {
    event?.stopPropagation();
    if (this.readOnly() || !id) return;
    const next = new Set(this.selectedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const list = [...next];
    this.selectedIds.set(list);
    this.partsSelected.emit(list);
  }
}
